import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	StatusIndicator,
	Button,
	Alert
} from '@cloudscape-design/components'
import { Table } from '@aws-northstar/ui/components'

import LoadingSpinner from '../../components/LoadingSpinner'

import type { Cluster } from '../../types/cluster'

import useClusters from '../../hooks/query/useClusters'
import useMultiTenantRequests from '../../hooks/query/useMultiTenantRequests'

import { useNewClusterFormStore } from '../../stores/clusters/newClusterFormStore'

// Per-row component so each cluster fetches its own tenant count independently
const MultiTenantCell: FC<{ clusterKey: string }> = ({ clusterKey }) => {
	const { data: tenants } = useMultiTenantRequests(clusterKey)

	const pendingCount = (tenants ?? []).filter(
		(t) => t.status === 'PENDING_APPROVAL'
	).length

	return <>{pendingCount > 0 ? `${pendingCount} Pending Approval` : 'None'}</>
}

const Clusters: FC = () => {
	const navigate = useNavigate()

	const { data: clusters, isLoading, isError } = useClusters()

	const newClusterFormStore = useNewClusterFormStore()

	const items = clusters ?? []

	if (isLoading) return <LoadingSpinner />

	const columnDefinitions = [
		{
			id: 'workloadClusterName',
			width: 280,
			header: 'Workload Cluster Name',
			cell: (data: Cluster) => (
				<Button
					variant="link"
					onClick={() =>
						navigate(
							`/clusters/${data.workloadRepoNameEnv ?? data.clusterID}`
						)
					}
				>
					{data.details?.clusterName ?? data.clusterID}
				</Button>
			)
		},

		{
			id: 'environment',
			width: 100,
			header: 'Environment',
			cell: (data: Cluster) => data.details?.environment ?? '-'
		},

		{
			id: 'distributionList',
			width: 260,
			header: 'Distribution List',
			cell: (data: Cluster) => data.details?.distributionList ?? '-'
		},

		{
			id: 'requestor',
			width: 180,
			header: 'Requested By',
			cell: (data: Cluster) => data.requestorID
		},

		{
			id: 'creationTime',
			width: 140,
			header: 'Created At',
			cell: (data: Cluster) =>
				data.creationTime
					? new Date(data.creationTime).toLocaleDateString()
					: '-'
		},

		{
			id: 'status',
			width: 120,
			header: 'Status',
			cell: (data: Cluster) => {
				switch (data.status) {
					case 'PENDING_APPROVAL':
						return (
							<StatusIndicator type="in-progress" colorOverride="blue">
								Pending Approval
							</StatusIndicator>
						)
					case 'APPROVED':
						return (
							<StatusIndicator type="success">Approved</StatusIndicator>
						)
					case 'REJECTED':
						return (
							<StatusIndicator type="error">Rejected</StatusIndicator>
						)
					default:
						return null
				}
			}
		},

		{
			id: 'multiTenantRequests',
			width: 180,
			header: 'Multi-Tenant Request',
			cell: (data: Cluster) => (
				<MultiTenantCell
					clusterKey={data.workloadRepoNameEnv ?? data.clusterID}
				/>
			)
		}
	]

	const tableActions = (
		<Button
			variant="primary"
			onClick={() => {
				newClusterFormStore.actions.clearFormValues()
				navigate('/clusters/new')
			}}
		>
			New Cluster Request
		</Button>
	)

	return (
		<>
			{isError && (
				<Alert type="warning" header="Backend unavailable">
					Clusters API is not available. Ensure{' '}
					<code>REACT_APP_API_URL</code> in .env points to the actual backend
					and that the service is reachable. You can still open "New Cluster
					Request" to view the form.
				</Alert>
			)}

			<Table
				header="Clusters"
				columnDefinitions={columnDefinitions}
				items={items}
				actions={tableActions}
				disableRowSelect
				sortingDisabled
			/>
		</>
	)
}

export default Clusters
